import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useRef, useState } from 'react'
import { ArrowLeft, Send, MessageSquare, Link as LinkIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { EntryCard } from '../components/Entry/EntryCard'
import { useNostrEvents } from '../hooks/useNostrEvents'
import { useNostrTopics } from '../hooks/useNostrTopics'
import { publishEntry } from '../lib/nostr'
import { EntryToolbar } from '../components/Entry/EntryToolbar'
import { parseTopicSlug, isValidUUID } from '../lib/urlUtils'
import { useAdmin } from '../contexts/AdminContext'

interface Topic {
  id: string
  title: string
  slug: string
  entry_count: number
  is_locked: boolean
  created_by_username: string | null
}

export function TopicPage() {
  const params = useParams<{ slug?: string; topicSlug?: string }>()
  const { pubkey, sk } = useAuth()
  const { settings } = useAdmin()
  const [newEntry, setNewEntry] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Determine which slug format we're using
  const slug = params.topicSlug || params.slug
  const parsed = slug ? parseTopicSlug(slug) : null

  // Determine the actual topic title to query
  // Prioritize the ID part if it carries the original title (not a UUID or number)
  const topicTitle = (parsed?.id && !isValidUUID(parsed.id) && isNaN(Number(parsed.id)))
    ? parsed.id
    : (parsed?.title || slug?.split('--')[0].replace(/-/g, ' ') || 'gündem')

  const { events, isLoading: entriesLoading } = useNostrEvents(topicTitle)
  const { topics: allTopics } = useNostrTopics()

  // Mock topic info for UI consistency
  const topic: Topic = {
    id: topicTitle,
    title: topicTitle,
    slug: slug || 'gundem',
    entry_count: events.length,
    is_locked: false,
    created_by_username: null,
  }

  const createEntryMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!pubkey) throw new Error('User not found')
      return await publishEntry(content, topicTitle, sk)
    },
    onSuccess: () => {
      // In Nostr, we rely on relay subscription to update. 
      // But we can manually trigger a refetch if using a Query pattern.
      // Here useNostrEvents is a live subscription.
      setNewEntry('')
    },
  })

  const handleSubmitEntry = (e: React.FormEvent) => {
    e.preventDefault()
    if (newEntry.trim() && newEntry.trim().length >= 10) {
      createEntryMutation.mutate(newEntry.trim())
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  // Normalize Nostr events to Entry interface expected by EntryCard
  const mappedEntries = events.map(event => ({
    id: event.id,
    content: event.content,
    created_at: new Date(event.created_at * 1000).toISOString(),
    author_username: event.pubkey.slice(0, 8),
    author_pubkey: event.pubkey,
    author_avatar_url: null as string | null,
    author_reputation: 0,
    upvotes: (event as any).upvotes || 0,
    downvotes: (event as any).downvotes || 0,
    userVotes: (event as any).userVotes || {},
    topicTitle: topicTitle,
  })).sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/"
          className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-poppins font-bold text-3xl lg:text-4xl text-gray-900 dark:text-white">
              {topic.title}
            </h1>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span>{topic.entry_count} entry</span>
            </div>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="space-y-4">
        {entriesLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="entry-card">
                  <div className="h-4 bg-surface-200 dark:bg-surface-700 rounded mb-3"></div>
                  <div className="h-4 bg-surface-200 dark:bg-surface-700 rounded mb-3"></div>
                  <div className="h-4 bg-surface-200 dark:bg-surface-700 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-4"
          >
            {mappedEntries.map((entry) => (
              <EntryCard key={entry.id} entry={entry} hideTopicHeader={true} />
            ))}

            {/* Dış Kaynaklar - Ekşi Sözlük ve Mavililer Facebook */}
            {mappedEntries.length > 0 && settings.features.externalResources && (
              <>
                <div className="flex items-center gap-2 py-4">
                  <div className="h-px flex-1 bg-surface-200 dark:bg-surface-700" />
                  <span className="text-xs font-poppins font-bold text-gray-400 uppercase tracking-widest">
                    Dış Kaynaklar
                  </span>
                  <div className="h-px flex-1 bg-surface-200 dark:bg-surface-700" />
                </div>

                <div className="space-y-3">
                  {/* Ekşi Sözlük */}
                  <a
                    href={`https://eksisozluk.com/${topicTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-çğıöşüâîû]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-gradient-to-r from-[#81C14B]/10 to-[#81C14B]/20 dark:from-[#81C14B]/10 dark:to-[#81C14B]/20 border border-[#81C14B]/30 dark:border-[#81C14B]/40 rounded-xl hover:border-[#81C14B]/60 dark:hover:border-[#81C14B]/70 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <LinkIcon className="w-5 h-5 text-[#81C14B] dark:text-[#81C14B] group-hover:scale-110 transition-transform flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-merriweather text-[#5a8f33] dark:text-[#9fd766]">
                          <span className="font-semibold">{topicTitle}</span> başlığını <span className="font-bold">Ekşi Sözlük</span>'te görüntüle
                        </div>
                      </div>
                    </div>
                  </a>

                  {/* Mavililer Facebook Grubu */}
                  <a
                    href={`https://www.facebook.com/groups/1654346078123482/search/?q=${encodeURIComponent(topicTitle)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-gradient-to-r from-[#1877F2]/10 to-[#1877F2]/20 dark:from-[#1877F2]/10 dark:to-[#1877F2]/20 border border-[#1877F2]/30 dark:border-[#1877F2]/40 rounded-xl hover:border-[#1877F2]/60 dark:hover:border-[#1877F2]/70 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <LinkIcon className="w-5 h-5 text-[#1877F2] dark:text-[#1877F2] group-hover:scale-110 transition-transform flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-merriweather text-[#0e5ac7] dark:text-[#5b9fff]">
                          <span className="font-semibold">{topicTitle}</span> başlığını <span className="font-bold">Mavililer</span> grubunda ara
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              </>
            )}

            {mappedEntries.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative overflow-hidden p-8 md:p-12 bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-700 shadow-xl"
              >
                {/* Background Decoration */}
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-mavi-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-kirmizi-500/5 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center text-center max-w-xl mx-auto space-y-8">
                  <div className="w-20 h-20 bg-mavi-50 dark:bg-mavi-900/30 rounded-2xl flex items-center justify-center rotate-3 border border-mavi-100 dark:border-mavi-800 shadow-inner">
                    <MessageSquare className="w-10 h-10 text-mavi-600" />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">
                        "{topicTitle}" başlığı henüz boş
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 font-merriweather italic text-sm">
                        bu konuda henüz kimse fikir beyan etmemiş.
                      </p>
                    </div>

                    {/* Suggestions (Did you mean?) */}
                    {allTopics.filter(t => t.title.toLowerCase().includes(topicTitle.toLowerCase().slice(0, 3))).length > 0 && (
                      <div className="pt-4 space-y-2">
                        <p className="text-[10px] font-poppins font-bold text-gray-400 uppercase tracking-widest">
                          Şunlardan birini mi demek istediniz?
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {allTopics
                            .filter(t => t.title.toLowerCase() !== topicTitle.toLowerCase() && t.title.toLowerCase().includes(topicTitle.toLowerCase().slice(0, 3)))
                            .slice(0, 3)
                            .map(t => (
                              <Link
                                key={t.id}
                                to={`/topic/${t.slug}`}
                                className="px-3 py-1 bg-surface-100 dark:bg-surface-800 hover:bg-mavi-500 hover:text-white rounded-full text-xs font-medium transition-all"
                              >
                                {t.title}
                              </Link>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="w-full pt-8 border-t border-surface-100 dark:border-surface-800">
                    {!pubkey ? (
                      <div className="space-y-4">
                        <p className="text-sm font-poppins font-medium text-mavi-600">
                          Bu başlığın tozunu almak için önce giriş yapmalısın.
                        </p>
                        <Link to="/login" className="btn-primary inline-block px-12 py-3 rounded-2xl shadow-lg shadow-mavi-500/25">
                          Giriş Yap ve Yaz
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <h4 className="font-poppins font-semibold text-gray-700 dark:text-gray-300">
                          Hadi, bu başlığı ilk sen doldur:
                        </h4>

                        <form onSubmit={handleSubmitEntry} className="space-y-4 text-left">
                          <div className="relative w-full rounded-2xl border-2 border-surface-100 dark:border-surface-800 bg-white dark:bg-surface-900 focus-within:bg-white dark:focus-within:bg-surface-900 focus-within:ring-4 focus-within:ring-mavi-500/10 focus-within:border-mavi-500 transition-all overflow-hidden group">
                            {/* Toolbar Section */}
                            <div className="px-3 py-2 border-b border-surface-200 dark:border-surface-700/50 bg-surface-100/50 dark:bg-surface-800/50">
                              <EntryToolbar
                                textareaRef={textareaRef}
                                value={newEntry}
                                onChange={setNewEntry}
                              />
                            </div>

                            {/* Textarea Section */}
                            <div className="relative">
                              <textarea
                                ref={textareaRef}
                                value={newEntry}
                                onChange={(e) => setNewEntry(e.target.value)}
                                placeholder="Buraya bir şeyler karala..."
                                className="w-full p-6 bg-transparent focus:outline-none resize-none transition-all font-merriweather leading-relaxed text-gray-800 dark:text-gray-200"
                                rows={5}
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] text-gray-400 font-poppins">
                              En az 3 karakter girmelisin.
                            </p>
                            <button
                              type="submit"
                              disabled={!newEntry.trim() || newEntry.trim().length < 3 || createEntryMutation.isPending}
                              className="btn-primary px-8 py-3 rounded-xl flex items-center gap-2 shadow-lg shadow-mavi-500/20"
                            >
                              {createEntryMutation.isPending ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                              Gitti Bile
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>

      {/* New Entry Form - Moved to Bottom */}
      {pubkey && mappedEntries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <form onSubmit={handleSubmitEntry} className="space-y-4">
            <div className="relative w-full rounded-2xl border-2 border-surface-100 dark:border-surface-800 bg-white dark:bg-surface-900 focus-within:bg-white dark:focus-within:bg-surface-900 focus-within:ring-4 focus-within:ring-mavi-500/10 focus-within:border-mavi-500 transition-all overflow-hidden group">
              {/* Toolbar Section */}
              <div className="px-3 py-2 border-b border-surface-200 dark:border-surface-700/50 bg-surface-100/50 dark:bg-surface-800/50">
                <EntryToolbar
                  textareaRef={textareaRef}
                  value={newEntry}
                  onChange={setNewEntry}
                />
              </div>

              {/* Textarea Section */}
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={newEntry}
                  onChange={(e) => setNewEntry(e.target.value)}
                  placeholder="Bu başlık hakkında ne düşünüyorsunuz? (En az 10 karakter)"
                  className="w-full p-4 pr-12 bg-transparent focus:outline-none resize-none transition-all font-merriweather text-gray-800 dark:text-gray-200"
                  rows={4}
                  minLength={10}
                  maxLength={2000}
                />
                <div className="absolute bottom-3 right-3 text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {newEntry.length}/2000
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!newEntry.trim() || newEntry.trim().length < 10 || createEntryMutation.isPending}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createEntryMutation.isPending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                Gönder
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </motion.div>
  )
}
